import Vue from 'vue'
import axios from 'axios'
import * as _ from 'lodash'
import { hydrateStop, hydrateShift, hydrateOrder } from '@/utils/etaCache'

const ORDER_STATUS = {
    ACTIVE: 30,
    IN_PROGRESS: 35,
    COMPLETED: 40,
    CANCELLED: 50,
}

const ROUTE_STATUS = {
    ACTIVE: 10,
    FINISHED: 20,
}

const ROUTE_PROGRESS_STATUS = {
    PENDING: 10,
    ACTIVE: 20,
    COMPLETED: 30,
}

let _socketBound = false
let _onOrderCreated = null
let _onOrderCollected = null
let _onOrderDelivered = null
let _onOrderCancelled = null
let _onOrderReordered = null

export default {
    state: {
        drivershareShifts: [],
    },

    mutations: {
        setDrivershareShifts(state, payload) {
            ;(payload || []).forEach(hydrateShift)
            state.drivershareShifts = payload
        },

        addDrivershareShift(state, payload) {
            const exists = state.drivershareShifts.some(item => +item.id === +payload.id)
            if (exists) return
            state.drivershareShifts.unshift(hydrateShift(payload))
        },

        editDrivershareShift(state, payload) {
            const index = state.drivershareShifts.findIndex(item => +item.id === +payload.id)
            if (index !== -1) {
                Vue.set(state.drivershareShifts, index, {
                    ...state.drivershareShifts[index],
                    ...hydrateShift(payload)
                })
            }
        },

        removeDrivershareShift(state, id) {
            state.drivershareShifts = state.drivershareShifts.filter(item => +item.id !== +id)
        },

        updateDrivershareShiftStatus(state, { ids, status }) {
            state.drivershareShifts.forEach((shift, index) => {
                if (ids.includes(shift.id)) {
                    Vue.set(state.drivershareShifts, index, { ...shift, status })
                }
            })
        },

        addDrivershareShifts(state, payload) {
            state.drivershareShifts = [...state.drivershareShifts, ...(payload || []).map(hydrateShift)]
        },

        addOrderToShift(state, { shift_id, order }) {
            const shift = state.drivershareShifts.find(s => +s.id === +shift_id)
            if (!shift) return
            // remember / restore order_total (survives page refresh)
            hydrateOrder(order)
            if (!shift.orders) Vue.set(shift, 'orders', [])

            const idx = shift.orders.findIndex(o => +o.id === +order.id)
            if (idx === -1) {
                shift.orders.unshift(order)
            } else {
                const existing = shift.orders[idx]
                const merged = { ...existing, ...order }

                if (order.stops && existing.stops) {
                    const stops = existing.stops.map(s => ({ ...s }))
                    order.stops.forEach(incoming => {
                        const sIdx = stops.findIndex(s => +s.id === +incoming.id)
                        if (sIdx === -1) {
                            stops.push(incoming)
                        } else {
                            stops[sIdx] = { ...stops[sIdx], ...incoming }
                        }
                    })
                    merged.stops = stops
                }

                Vue.set(shift.orders, idx, merged)
            }
        },

        upsertRouteStops(state, { shift_id, route_id, stops, routeInfo }) {
            if (!route_id || !stops || !stops.length) return
            const shift = state.drivershareShifts.find(s => +s.id === +shift_id)
            if (!shift) return
            if (!shift.routes) Vue.set(shift, 'routes', [])

            const existingIdx = shift.routes.findIndex(r => +r.id === +route_id)
            let route

            const safeRouteInfo = routeInfo
                ? (({ stops: _ignoredStops, shift: _ignoredShift, ...rest }) => rest)(routeInfo)
                : null

            if (existingIdx === -1) {
                const hasOtherActiveRoute = shift.routes.some(
                    r => +r.id !== +route_id && Number(r.status_in_progress) === ROUTE_PROGRESS_STATUS.ACTIVE
                )
                route = {
                    id: route_id,
                    shift_id,
                    status: 20,
                    status_in_progress: hasOtherActiveRoute
                        ? ROUTE_PROGRESS_STATUS.PENDING
                        : ROUTE_PROGRESS_STATUS.ACTIVE,
                    created_at: stops[0].created_at,
                    updated_at: stops[0].updated_at,
                    ...(safeRouteInfo || {}),
                    stops: [],
                }
                shift.routes.push(route)
            } else {
                route = { ...shift.routes[existingIdx], ...(safeRouteInfo || {}) }
                if (!route.stops) route.stops = []
                Vue.set(shift.routes, existingIdx, route)
            }

            stops.forEach(stop => {
                const { route: _embeddedRoute, order: _embeddedOrder, ...cleanStop } = stop
                if (cleanStop.eta == null) delete cleanStop.eta

                const stopIdx = route.stops.findIndex(s => +s.id === +cleanStop.id)
                if (stopIdx === -1) {
                    route.stops.push(hydrateStop(cleanStop))
                } else {
                    Vue.set(route.stops, stopIdx, hydrateStop({ ...route.stops[stopIdx], ...cleanStop }))
                }
            })
        },

        updateOrderStopStatus(state, { shift_id, order_id, stop_type, patch }) {
            const shift = state.drivershareShifts.find(s => +s.id === +shift_id)
            if (!shift) return

            if (shift.orders) {
                const order = shift.orders.find(o => +o.id === +order_id)
                if (order && order.stops) {
                    const stopIdx = order.stops.findIndex(s => s.type === stop_type)
                    if (stopIdx !== -1) {
                        Vue.set(order.stops, stopIdx, { ...order.stops[stopIdx], ...patch })
                    }
                }
            }

            if (shift.routes) {
                shift.routes.forEach(route => {
                    if (!route.stops) return
                    const stopIdx = route.stops.findIndex(s => +s.order_id === +order_id && s.type === stop_type)
                    if (stopIdx !== -1) {
                        Vue.set(route.stops, stopIdx, { ...route.stops[stopIdx], ...patch })
                    }
                })
            }
        },

        setOrderStatus(state, { shift_id, order_id, status }) {
            const shift = state.drivershareShifts.find(s => +s.id === +shift_id)
            if (!shift || !shift.orders) return
            const order = shift.orders.find(o => +o.id === +order_id)
            if (order) Vue.set(order, 'status', status)
        },

        setOrderCancelled(state, { shift_id, order_id }) {
            const shift = state.drivershareShifts.find(s => +s.id === +shift_id)
            if (!shift) return

            const stopPatch = {
                status: ORDER_STATUS.CANCELLED,
                sequence: null,
                duration: null,
                eta: null,
            }

            const order = (shift.orders || []).find(o => +o.id === +order_id)

            if (order) {
                Vue.set(order, 'status', ORDER_STATUS.CANCELLED)
                ;(order.stops || []).forEach((stop, i) => {
                    Vue.set(order.stops, i, { ...stop, ...stopPatch })
                })
            }

            const cancelledStops = []

            ;(shift.routes || []).forEach(route => {
                ;(route.stops || []).forEach((stop, i) => {
                    if (+stop.order_id !== +order_id) return
                    const next = { ...stop, ...stopPatch }
                    Vue.set(route.stops, i, next)
                    cancelledStops.push(next)
                })
            })

            if (!order && cancelledStops.length) {
                if (!shift.orders) Vue.set(shift, 'orders', [])

                const pickup = cancelledStops.find(s => s.type === 'pickup') || cancelledStops[0]

                shift.orders.unshift({
                    id: order_id,
                    shift_id: shift.id,
                    restaurant_id: pickup.restaurant_id,
                    created_at: pickup.created_at,
                    status: ORDER_STATUS.CANCELLED,
                    stops: cancelledStops,
                })
            }
        },

        setRouteStatus(state, { shift_id, route_id, status, status_in_progress }) {
            const shift = state.drivershareShifts.find(s => +s.id === +shift_id)
            if (!shift) return

            const patchRoute = (route) => {
                if (status !== undefined) Vue.set(route, 'status', status)
                if (status_in_progress !== undefined) Vue.set(route, 'status_in_progress', status_in_progress)
            }

            const route = (shift.routes || []).find(r => +r.id === +route_id)
            if (route) patchRoute(route)

            const activeRoute = (shift.active_routes || []).find(r => +r.id === +route_id)
            if (activeRoute) patchRoute(activeRoute)

            if (status_in_progress === ROUTE_PROGRESS_STATUS.COMPLETED && shift.routes) {
                const nextPending = shift.routes
                    .filter(r => +r.id !== +route_id && Number(r.status_in_progress) === ROUTE_PROGRESS_STATUS.PENDING)
                    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0]
                if (nextPending) {
                    Vue.set(nextPending, 'status_in_progress', ROUTE_PROGRESS_STATUS.ACTIVE)
                }
            }
        },
    },

    actions: {

        // FETCH
        async fetchDrivershareShifts({ commit, dispatch }, params) {
            commit('clearError')
            commit('setLoading', true)

            const user = JSON.parse(localStorage.getItem('user'))

            try {
                const { data } = await axios.get(
                    `${this._vm.appConfig.app_url}/api/drivershare-shifts`,
                    {
                        params,
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Authorization': `Bearer ${user.access_token}`
                        }
                    }
                )

                commit('setDrivershareShifts', data)
                commit('setLoading', false)
            } catch (error) {
                commit('setLoading', false)
                dispatch('_handleDrivershareError', { error, dispatch })
            }
        },

        //CREATE
        async createDrivershareShift({ commit, dispatch }, payload) {
            commit('clearError')
            commit('setLoading', true)

            const user = JSON.parse(localStorage.getItem('user'))

            try {
                const { data } = await axios.post(
                    `${this._vm.appConfig.app_url}/api/drivershare-shifts`,
                    payload,
                    {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Authorization': `Bearer ${user.access_token}`
                        }
                    }
                )

                commit('setLoading', false)
                commit('addDrivershareShift', data.shift)
                return data.shift
            } catch (error) {
                commit('setLoading', false)
                dispatch('_handleDrivershareError', { error, dispatch })
                throw error
            }
        },

        // UPDATE 
        async updateDrivershareShift({ commit, dispatch }, payload) {
            commit('clearError')
            commit('setLoading', true)

            const user = JSON.parse(localStorage.getItem('user'))

            try {
                const { data } = await axios.put(
                    `${this._vm.appConfig.app_url}/api/drivershare-shifts/${payload.id}`,
                    payload,
                    {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Authorization': `Bearer ${user.access_token}`
                        }
                    }
                )

                commit('setLoading', false)
                commit('editDrivershareShift', data.shift)
                return data.shift
            } catch (error) {
                commit('setLoading', false)
                dispatch('_handleDrivershareError', { error, dispatch })
                throw error
            }
        },

        // DELETE
        async deleteDrivershareShift({ commit, dispatch }, id) {
            commit('clearError')
            commit('setLoading', true)

            const user = JSON.parse(localStorage.getItem('user'))

            try {
                await axios.delete(
                    `${this._vm.appConfig.app_url}/api/drivershare-shifts/${id}`,
                    {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Authorization': `Bearer ${user.access_token}`
                        }
                    }
                )

                commit('setLoading', false)
                commit('removeDrivershareShift', id)
            } catch (error) {
                commit('setLoading', false)
                dispatch('_handleDrivershareError', { error, dispatch })
                throw error
            }
        },

        async cancelDrivershareOrder({ commit, dispatch }, { id, driver_id, shift_id }) {
            commit('clearError')
            commit('setLoading', true)

            const user = JSON.parse(localStorage.getItem('user'))

            try {
                const { data } = await axios.post(
                    `${this._vm.appConfig.app_url}/api/drivershare/cancel-order`,
                    { id, driver_id },
                    {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Authorization': `Bearer ${user.access_token}`
                        }
                    }
                )

                commit('setLoading', false)
                commit('setOrderCancelled', { shift_id, order_id: id })
                return data
            } catch (error) {
                commit('setLoading', false)
                dispatch('_handleDrivershareError', { error, dispatch })
                throw error
            }
        },

        async closeDrivershareRoute({ commit, dispatch }, { route_id, shift_id }) {
            commit('clearError')
            commit('setLoading', true)

            const user = JSON.parse(localStorage.getItem('user'))

            try {
                const { data } = await axios.post(
                    `${this._vm.appConfig.app_url}/api/drivershare/routes/close`,
                    { route_id },
                    {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Authorization': `Bearer ${user.access_token}`
                        }
                    }
                )

                commit('setLoading', false)
                commit('setRouteStatus', {
                    shift_id,
                    route_id,
                    status: ROUTE_STATUS.FINISHED,
                    status_in_progress: ROUTE_PROGRESS_STATUS.COMPLETED,
                })
                return data
            } catch (error) {
                commit('setLoading', false)
                dispatch('_handleDrivershareError', { error, dispatch })
                throw error
            }
        },

        async reorderDrivershareRouteStops({ commit, dispatch }, { route_id, shift_id, driver_id, stops }) {
            commit('clearError')
            commit('setLoading', true)

            const user = JSON.parse(localStorage.getItem('user'))

            try {
                const { data } = await axios.post(`${this._vm.appConfig.app_url}/api/drivershare/routes/reorder-stops`,
                { route_id, driver_id, stops },
                    {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Authorization': `Bearer ${user.access_token}`
                        }
                    }
                )

                commit('setLoading', false)
                const confirmedStops = (data && data.stops) || stops
                commit('upsertRouteStops', { shift_id, route_id, stops: confirmedStops })
                return data
            } catch (error) {
                commit('setLoading', false)
                dispatch('_handleDrivershareError', { error, dispatch })
                throw error
            }
        },

        async publishDrivershareShifts({ commit, dispatch }, shiftGroupIds) {
            commit('clearError')
            commit('setLoading', true)

            const user = JSON.parse(localStorage.getItem('user'))

            try {
                const { data } = await axios.post(
                    `${this._vm.appConfig.app_url}/api/drivershare-shifts/publish`,
                    { shift_group_ids: shiftGroupIds },
                    {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Authorization': `Bearer ${user.access_token}`
                        }
                    }
                )

                commit('setLoading', false)
                commit('updateDrivershareShiftStatus', { ids: shiftGroupIds, status: 20 })
                return data
            } catch (error) {
                commit('setLoading', false)
                dispatch('_handleDrivershareError', { error, dispatch })
                throw error
            }
        },

        // UNPUBLISH
        async unpublishDrivershareShifts({ commit, dispatch }, shiftGroupIds) {
            commit('clearError')
            commit('setLoading', true)

            const user = JSON.parse(localStorage.getItem('user'))

            try {
                const { data } = await axios.post(
                    `${this._vm.appConfig.app_url}/api/drivershare-shifts/unpublish`,
                    { shift_group_ids: shiftGroupIds },
                    {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Authorization': `Bearer ${user.access_token}`
                        }
                    }
                )

                commit('setLoading', false)
                commit('updateDrivershareShiftStatus', { ids: shiftGroupIds, status: 10 })
                return data
            } catch (error) {
                commit('setLoading', false)
                dispatch('_handleDrivershareError', { error, dispatch })
                throw error
            }
        },

        // COPY 
        async copyDrivershareShifts({ commit, dispatch }, payload) {
            commit('clearError')
            commit('setLoading', true)

            const user = JSON.parse(localStorage.getItem('user'))

            try {
                const { data } = await axios.post(
                    `${this._vm.appConfig.app_url}/api/drivershare-shifts/copy`,
                    payload,
                    {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Authorization': `Bearer ${user.access_token}`
                        }
                    }
                )

                commit('setLoading', false)
                if (data.data && data.data.length) {
                    commit('addDrivershareShifts', data.data)
                }

                return data
            } catch (error) {
                commit('setLoading', false)
                dispatch('_handleDrivershareError', { error, dispatch })
                throw error
            }
        },

        bindDrivershareSocketEvents({ commit, state, dispatch }) {
            const connection = this.state.socketObserver && this.state.socketObserver.connection
            if (!connection) {
                return
            }
            if (_socketBound) return

            const extractEntries = (message) => {
                const data = message && message.data
                if (!data) return []

                const raw = data.orders !== undefined ? data.orders : data.order
                if (!raw) return []

                const buckets = Array.isArray(raw) ? raw : [raw]

                return buckets.flatMap(bucket => {
                    if (!bucket || typeof bucket !== 'object') return []

                    if (bucket.stops || (bucket.id && bucket.order === undefined)) {
                        return [{ order: bucket, route: bucket.route || null }]
                    }

                    const route =
                        (bucket.route_id && typeof bucket.route_id === 'object' ? bucket.route_id : null) ||
                        (bucket.route && typeof bucket.route === 'object' ? bucket.route : null)

                    const orders = Array.isArray(bucket.order) ? bucket.order : [bucket.order]
                    return orders
                        .filter(Boolean)
                        .map(order => ({ order, route }))
                })
            }

            const syncOrderFromEvent = ({ order, route }, { defaultOrderStatus, routeId: fallbackRouteId } = {}) => {
                if (!order) return

                const shiftId = Number(order.shift_id || (route && route.shift_id))
                if (!shiftId) {
                    console.warn('[drivershare] socket event has no shift_id — skipped', order)
                    return
                }

                const rawStops = order.stops || []
                const hasUsableStops =
                    Array.isArray(rawStops) && rawStops.length > 0 && rawStops.every(s => s && s.type)

                if (!hasUsableStops) {
                    console.warn(
                        `[drivershare] socket event for order #${order.id} has no usable stop data — skipped`,
                        order
                    )
                    return
                }

                const routeId =
                    (route && route.id) ||
                    rawStops[0].route_id ||
                    (rawStops[0].route && rawStops[0].route.id) ||
                    fallbackRouteId ||
                    null

                const stops = rawStops.map(({ route: _r, order: _o, ...rest }) => {
                    const next = {
                        ...rest,
                        route_id: rest.route_id || routeId,
                    }
                    if (next.eta == null) delete next.eta
                    return hydrateStop(next)
                })

                const ownStops = stops.filter(s => Number(s.order_id) === Number(order.id))

                const orderPatch = {
                    id: order.id,
                    shift_id: shiftId,
                    restaurant_id: order.restaurant_id,
                    delivery_lat: order.delivery_lat,
                    delivery_lng: order.delivery_lng,
                    delivery_address: order.delivery_address,
                    order_quantity: order.order_quantity,
                    created_at: order.created_at,
                    // driver earning estimate (only on events that carry it)
                    order_total: order.order_total,
                    stops: ownStops.length ? ownStops : stops,
                }

                if (defaultOrderStatus !== undefined) {
                    orderPatch.status = defaultOrderStatus
                } else if (order.status !== undefined) {
                    orderPatch.status = order.status
                } else if (ownStops.length && ownStops.every(s => Number(s.status) === ORDER_STATUS.COMPLETED)) {
                    orderPatch.status = ORDER_STATUS.COMPLETED
                }

                Object.keys(orderPatch).forEach(key => orderPatch[key] === undefined && delete orderPatch[key])

                commit('addOrderToShift', { shift_id: shiftId, order: orderPatch })

                rawStops.forEach(s => {
                    const sibling = s && s.order
                    if (!sibling || !sibling.id || Number(sibling.id) === Number(order.id)) return

                    const siblingStops = stops.filter(x => Number(x.order_id) === Number(sibling.id))
                    commit('addOrderToShift', {
                        shift_id: shiftId,
                        order: {
                            id: sibling.id,
                            shift_id: sibling.shift_id || shiftId,
                            restaurant_id: sibling.restaurant_id,
                            delivery_lat: sibling.delivery_lat,
                            delivery_lng: sibling.delivery_lng,
                            delivery_address: sibling.delivery_address,
                            order_quantity: sibling.order_quantity,
                            status: sibling.status,
                            created_at: sibling.created_at,
                            stops: siblingStops,
                        },
                    })
                })

                if (routeId) {
                    const routeInfo = route
                        ? (({ stops: _s, shift: _sh, ...rest }) => rest)(route)
                        : null
                    commit('upsertRouteStops', { shift_id: shiftId, route_id: routeId, stops, routeInfo })
                } else {
                    console.warn('[drivershare] socket event carries no route id — route list not updated', order)
                }
            }

            const handle = (message, opts) => {
                extractEntries(message).forEach(entry => syncOrderFromEvent(entry, opts))
            }
            const handleCancelled = (message) => {
                const data = message && message.data
                const cancelledId = data && data.id != null ? Number(data.id) : null

                extractEntries(message).forEach(entry => {
                    const { order, route } = entry
                    if (!order || !order.id) return

                    const orderStops = order.stops || []
                    const isCancelled =
                        Number(order.id) === cancelledId ||
                        Number(order.status) === ORDER_STATUS.CANCELLED ||
                        (orderStops.length > 0 &&
                            orderStops.every(s => Number(s.status) === ORDER_STATUS.CANCELLED))

                    if (isCancelled) {
                        const shiftId = Number(order.shift_id || (route && route.shift_id))
                        if (!shiftId) return
                        commit('setOrderCancelled', { shift_id: shiftId, order_id: order.id })
                    } else {
                        // Remaining orders on the route: refresh their sequence / ETA
                        syncOrderFromEvent(entry)
                    }
                })
            }

            const handleReordered = (message) => {
                const routeId = message && message.data
                    ? (message.data.route_id || message.data.id)
                    : null
                handle(message, { routeId })
            }

            // Order could not be placed on a route (e.g. 1 hour SLA): show the reason
            const handleRejected = (message) => {
                const data = message && message.data
                const result = data && data.orders
                if (!result || Array.isArray(result) || result.success !== false) return false

                // only for shifts loaded in this panel
                const shiftId = Number(data.id)
                const isOurShift = state.drivershareShifts.some(s => +s.id === shiftId)
                if (isOurShift) {
                    dispatch('setError', result.message
                        || `Order #${result.order_id} could not be assigned to a route`)
                }
                return true
            }

            _onOrderCreated = (message) => {
                if (handleRejected(message)) return
                handle(message, { defaultOrderStatus: ORDER_STATUS.ACTIVE })
            }
            _onOrderCollected = (message) => handle(message)
            _onOrderDelivered = (message) => handle(message)
            _onOrderCancelled = (message) => handleCancelled(message)
            _onOrderReordered = (message) => handleReordered(message)

            connection.on('orderCreated', _onOrderCreated)
            connection.on('orderCollected', _onOrderCollected)
            connection.on('orderDelivered', _onOrderDelivered)
            connection.on('orderCancelled', _onOrderCancelled)
            connection.on('orderReordered', _onOrderReordered)

            _socketBound = true
        },

        unbindDrivershareSocketEvents() {
            // Root state — socketObserver lives there, not in this module's state
            const connection = this.state.socketObserver && this.state.socketObserver.connection
            if (!connection || !_socketBound) return
            connection.off('orderCreated', _onOrderCreated)
            connection.off('orderCollected', _onOrderCollected)
            connection.off('orderDelivered', _onOrderDelivered)
            connection.off('orderCancelled', _onOrderCancelled)
            connection.off('orderReordered', _onOrderReordered)
            _socketBound = false
        },

        _handleDrivershareError({ dispatch }, { error }) {
            if (error.response) {
                const { status, data } = error.response
                if (status === 401) {
                    dispatch('logOutUser')
                    window.location.reload()
                    return
                }
                if (status === 400 || status === 422) {
                    const msg = data.message || _.values(data.errors || data)[0]
                    dispatch('setError', Array.isArray(msg) ? msg[0] : msg)
                    return
                }
            }
            dispatch('setError', error.message || error)
        },
    },

    getters: {
        drivershareShifts(state) {
            return state.drivershareShifts
        }
    }
}